"use client"

import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts"
import { GenerativeAvatar } from "@/components/GenerativeAvatar"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { type ChartConfig, ChartContainer, ChartTooltip } from "@/components/ui/chart"
import { getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

// Constants for avatar rendering strategy
const MIN_BAR_WIDTH_FOR_AVATARS = 28 // Minimum bar width to render avatars below bars
const DEFAULT_AVATAR_SIZE = 28 // Default avatar size for hover mode
const MAX_AVATAR_SIZE = 28 // Maximum avatar size (cap at 28px)
const Y_AXIS_RESERVED_WIDTH = 40 // Width reserved for Y-axis
const MAX_BAR_WIDTH = 60 // Maximum width for individual bars (Recharts default)
const BAR_SPACING_RATIO = 0.9 // Recharts barCategoryGap spacing (10% gap)
const BAR_MARGIN = 8 // Additional margin between bars

// Calculate avatar rendering strategy based on bar width
function calculateAvatarStrategy(
  containerWidth: number,
  dataLength: number,
  margins: { left: number; right: number },
): {
  barWidth: number
  renderMode: "always" | "hover" // Always show or show on hover
  avatarSize: number
} {
  if (dataLength === 0) {
    return { barWidth: 0, renderMode: "always", avatarSize: DEFAULT_AVATAR_SIZE }
  }

  // Calculate available width for bars
  const availableWidth = containerWidth - margins.left - margins.right - Y_AXIS_RESERVED_WIDTH

  // Calculate bar width (Recharts uses maxBarSize, but actual width depends on spacing)
  // Recharts automatically spaces bars with barCategoryGap (default 10%)
  // So we estimate: available width / count, minus spacing gap, capped at maxBarSize
  const estimatedBarWidth = Math.min(
    MAX_BAR_WIDTH,
    (availableWidth / dataLength) * BAR_SPACING_RATIO - BAR_MARGIN,
  )

  if (estimatedBarWidth >= MIN_BAR_WIDTH_FOR_AVATARS) {
    // Bar is wide enough: render avatar with size capped at MAX_AVATAR_SIZE
    return {
      barWidth: estimatedBarWidth,
      renderMode: "always",
      avatarSize: Math.min(MAX_AVATAR_SIZE, Math.floor(estimatedBarWidth)),
    }
  }

  // Bar is too narrow: show avatar on hover with fixed size
  return {
    barWidth: estimatedBarWidth,
    renderMode: "hover",
    avatarSize: DEFAULT_AVATAR_SIZE,
  }
}

// Helper to safely encode GitHub handles for URLs
function encodeGitHubHandle(handle: string): string {
  return encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))
}

// Helper to get GitHub avatar URL with validation
function getGitHubAvatarUrl(handle: string, size = 32): string {
  const safeHandle = encodeGitHubHandle(handle)
  return `https://github.com/${safeHandle}.png?size=${size}`
}

interface ColorCodedBarChartProps {
  title: string
  description?: string
  data: Array<{
    label: string
    value: number
    color: "primary" | "secondary" // Which color to use for this bar
  }>
  primaryLabel: string // Label for primary color items
  secondaryLabel: string // Label for secondary color items
  theme?: ThemeConfigType
  className?: string
  renderAvatars?: boolean // Whether to render avatar images on x-axis
  avatarType?: "contributor" | "team" | "repository" // Type of avatar to render
  getOrganizationName?: (name: string) => string | undefined // For repositories, get organization name
}

// Custom tick component that renders avatars for contributors
const ContributorAvatarTick = ({
  x,
  y,
  payload,
  size = 24,
}: {
  x: number
  y: number
  payload: { value: string }
  size?: number
}) => {
  const handle = payload.value
  // Request 2x resolution for high-DPI/retina displays to ensure crisp rendering
  const avatarUrl = getGitHubAvatarUrl(handle, size * 2)
  const fallback = handle.slice(0, 2).toUpperCase()

  const halfSize = size / 2
  const fontSize = Math.max(8, size * 0.4)

  // Generate a consistent color for the fallback based on the handle
  const getColorFromHandle = (str: string): string => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }
    const hue = Math.abs(hash % 360)
    return `hsl(${hue}, 65%, 50%)`
  }

  return (
    <g transform={`translate(${x},${y})`}>
      {/* SVG fallback - always visible as a baseline */}
      <circle cx={0} cy={halfSize} r={halfSize} fill={getColorFromHandle(handle)} opacity={0.9} />
      <text
        x={0}
        y={halfSize}
        fontSize={fontSize}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontWeight="500"
      >
        {fallback}
      </text>
      {/* Try to load actual avatar image - will overlay if successful */}
      <foreignObject
        x={-halfSize}
        y={0}
        width={size}
        height={size}
        style={{ pointerEvents: "none" }}
      >
        <div
          className="flex items-center justify-center"
          style={{ width: `${size}px`, height: `${size}px` }}
        >
          <Avatar className="shrink-0" style={{ width: `${size}px`, height: `${size}px` }}>
            <AvatarImage src={avatarUrl} alt={handle} />
            <AvatarFallback
              delayMs={0}
              style={{ fontSize: `${fontSize}px`, backgroundColor: "transparent" }}
            >
              {/* Empty fallback since we have SVG fallback below */}
            </AvatarFallback>
          </Avatar>
        </div>
      </foreignObject>
    </g>
  )
}

// Custom tick component for generative avatars (teams/repositories)
const GenerativeAvatarTick = ({
  x,
  y,
  payload,
  type,
  getOrganizationName,
  size = 24,
}: {
  x: number
  y: number
  payload: { value: string }
  type: "team" | "repository"
  getOrganizationName?: (name: string) => string | undefined
  size?: number
}) => {
  const name = payload.value
  const orgName =
    type === "repository" && getOrganizationName ? getOrganizationName(name) : undefined

  const halfSize = size / 2

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-halfSize} y={0} width={size} height={size}>
        <div className="flex items-center justify-center">
          <GenerativeAvatar name={name} type={type} organizationName={orgName} size={size} />
        </div>
      </foreignObject>
    </g>
  )
}

export const ColorCodedBarChart = observer(
  ({
    title,
    description,
    data,
    primaryLabel,
    secondaryLabel,
    theme,
    className,
    renderAvatars = false,
    avatarType = "contributor",
    getOrganizationName,
  }: ColorCodedBarChartProps) => {
    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme

    // Ref to measure chart container width
    const containerRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    // Use a ref to track previous width to avoid stale closure issues
    // (comparing with state value in closure would always use initial 0)
    const previousWidthRef = useRef<number>(0)

    // Measure container width on mount, resize, and when data changes
    // Use ResizeObserver to detect when container becomes visible (e.g., tab switching)
    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth
          // Only update if width has actually changed to avoid unnecessary re-renders
          if (width !== previousWidthRef.current) {
            previousWidthRef.current = width
            setContainerWidth(width)
          }
        }
      }

      // Immediate measurement when effect runs (on mount or data change)
      updateWidth()

      // Use ResizeObserver to detect size changes (including visibility changes)
      const resizeObserver = new ResizeObserver(() => {
        updateWidth()
      })

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }

      // Also listen to window resize as fallback
      window.addEventListener("resize", updateWidth)

      return () => {
        resizeObserver.disconnect()
        window.removeEventListener("resize", updateWidth)
      }
    }, []) // Re-measure when data changes (e.g., switching insights, time ranges)

    // Calculate avatar rendering strategy based on container width and bar width
    // Memoized to ensure stable reference for useCallback dependencies
    const avatarStrategy = useMemo(
      () =>
        renderAvatars
          ? calculateAvatarStrategy(containerWidth, data.length, { left: 10, right: 10 })
          : null,
      [renderAvatars, containerWidth, data.length],
    )

    // Extract stable renderMode for useCallback dependency
    // Use ref for avatarSize to avoid recreating tick component when size changes
    const renderMode = avatarStrategy?.renderMode || "hover"
    const avatarSizeRef = useRef(DEFAULT_AVATAR_SIZE)
    avatarSizeRef.current = avatarStrategy?.avatarSize || DEFAULT_AVATAR_SIZE

    // Chart config with both colors
    const chartConfig: ChartConfig = {
      value: {
        label: "Value",
      },
      primary: {
        label: primaryLabel,
        color: "var(--chart-1)", // Primary color for team members
      },
      secondary: {
        label: secondaryLabel,
        color: "var(--chart-2)", // Secondary color for external
      },
    }

    // Function to get color based on data item
    const getBarColor = (color: "primary" | "secondary") => {
      return color === "primary" ? "var(--chart-1)" : "var(--chart-2)"
    }

    // Custom tick component that checks bar width and conditionally renders avatars
    // Memoized to prevent unnecessary re-renders and ensure Recharts renders all ticks consistently
    // Only recreates when renderMode changes, not when avatarSize changes (uses ref)
    const ConditionalAvatarTick = useCallback(
      (props: { x: number; y: number; payload: { value: string } } & Record<string, unknown>) => {
        // If bar width < MIN_BAR_WIDTH_FOR_AVATARS, don't render avatars (show in tooltip instead)
        if (renderMode === "hover") {
          // Return diagonal text tick for narrow bars
          return (
            <text
              x={props.x}
              y={props.y}
              fontSize="12px"
              textAnchor="end"
              fill="currentColor"
              className="text-muted-foreground"
              transform={`rotate(-45 ${props.x} ${props.y})`}
            >
              {props.payload.value}
            </text>
          )
        }

        // Bar is wide enough, render avatar (use current size from ref)
        if (avatarType === "contributor") {
          return (
            <ContributorAvatarTick
              x={props.x}
              y={props.y}
              payload={props.payload}
              size={avatarSizeRef.current}
            />
          )
        }

        return (
          <GenerativeAvatarTick
            x={props.x}
            y={props.y}
            payload={props.payload}
            type={avatarType}
            getOrganizationName={getOrganizationName}
            size={avatarSizeRef.current}
          />
        )
      },
      [renderMode, avatarType, getOrganizationName],
    )

    // Calculate appropriate height for x-axis based on avatar rendering
    const xAxisHeight =
      renderAvatars && renderMode === "always" ? Math.max(50, avatarSizeRef.current + 16) : 60

    return (
      <Card
        className={cn(
          "w-full overflow-hidden",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        <CardHeader>
          <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>{title}</CardTitle>
          {description && (
            <p
              className={cn(
                "text-sm text-muted-foreground",
                !isDefaultTheme && themeClasses.typography,
              )}
            >
              {description}
            </p>
          )}
        </CardHeader>
        <CardContent ref={containerRef}>
          <ChartContainer
            config={chartConfig}
            className="h-[300px] sm:h-[350px] md:h-[400px] w-full"
          >
            <BarChart data={data} accessibilityLayer>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tickLine={false}
                tickMargin={8}
                axisLine={false}
                interval={0}
                tick={renderAvatars ? ConditionalAvatarTick : { fontSize: "12px" }}
                angle={renderAvatars ? 0 : -45}
                textAnchor={renderAvatars ? "middle" : "end"}
                height={xAxisHeight}
              />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: "12px" }} />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const dataPoint = payload[0].payload as {
                      label: string
                      value: number
                      color: "primary" | "secondary"
                    }
                    const shouldShowAvatarInTooltip = renderAvatars && renderMode === "hover"
                    const colorLabel = dataPoint.color === "primary" ? primaryLabel : secondaryLabel

                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="flex flex-col gap-2">
                          {shouldShowAvatarInTooltip && (
                            <div className="flex items-center justify-center">
                              {avatarType === "contributor" ? (
                                <Avatar style={{ width: "40px", height: "40px" }}>
                                  <AvatarImage
                                    src={getGitHubAvatarUrl(dataPoint.label, 80)}
                                    alt={dataPoint.label}
                                  />
                                  <AvatarFallback style={{ fontSize: "16px" }}>
                                    {dataPoint.label.slice(0, 2).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                              ) : (
                                <GenerativeAvatar
                                  name={dataPoint.label}
                                  type={avatarType}
                                  organizationName={
                                    avatarType === "repository" && getOrganizationName
                                      ? getOrganizationName(dataPoint.label)
                                      : undefined
                                  }
                                  size="lg"
                                />
                              )}
                            </div>
                          )}
                          <div className="grid gap-2">
                            <div className="flex flex-col">
                              <span className="text-[0.70rem] uppercase text-muted-foreground">
                                {dataPoint.label} ({colorLabel})
                              </span>
                              <span className="font-bold text-muted-foreground">
                                {dataPoint.value}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((entry) => (
                  <Cell key={entry.label} fill={getBarColor(entry.color)} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    )
  },
)

ColorCodedBarChart.displayName = "ColorCodedBarChart"
